﻿import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import {
  RegistrationJourneyState,
  RegistrationJourneyContext,
  RegistrationJourneyContextType,
  DEFAULT_JOURNEY_STATE,
} from "./RegistrationJourneyContext";
import {
  serializeFiles,
  deserializeFiles,
  collectFileIds,
} from "./journeyStorage";
import { IndexedDb } from "utils/IndexedDb";
import { FullScreenLoader } from "components";

const STORAGE_KEY = "registrationJourney";

export const RegistrationJourneyContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [journey, setJourney] = useState<RegistrationJourneyState>(
    () => DEFAULT_JOURNEY_STATE
  );
  const [isLoading, setIsLoading] = useState(true);
  const initialized = useRef(false);

  // Load from storage on mount
  useEffect(() => {
    if (initialized.current) {
      return;
    }

    loadFromLocalStorage()
      .then((loaded) => {
        if (loaded) {
          setJourney({ ...loaded, loadedFromStorage: true });
        } else {
          setJourney((state) => ({ ...state, loadedFromStorage: true }));
        }
      })
      .catch((e) => console.error("Failed to load journey:", e))
      .finally(() => {
        setIsLoading(false);
      });
    initialized.current = true;
  }, []);

  // Save to storage on journey change
  useEffect(() => {
    if (!isLoading) {
      saveToLocalStorage(journey);
    }
  }, [journey, isLoading]);

  const contextValue: RegistrationJourneyContextType = useMemo(
    () => ({ journey, setJourney }),
    [journey]
  );

  return (
    <RegistrationJourneyContext.Provider value={contextValue}>
      {journey.loadedFromStorage ? <>{children}</> : <FullScreenLoader />}
    </RegistrationJourneyContext.Provider>
  );
};

async function loadFromLocalStorage(): Promise<RegistrationJourneyState | null> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const deserialized =
      await deserializeFiles<RegistrationJourneyState>(parsed);

    return {
      ...deserialized,
      createdOn: new Date(deserialized.createdOn as unknown as string),
    };
  } catch (e) {
    console.error("Failed to load from localStorage:", e);
    return null;
  }
}

async function saveToLocalStorage(
  data: RegistrationJourneyState
): Promise<void> {
  try {
    const serialized = await serializeFiles(data);
    const serializedJson = JSON.stringify(serialized);
    localStorage.setItem(STORAGE_KEY, serializedJson);

    // Cleanup orphaned files - collect IDs from the serialized (not deserialized) data
    const fileIds = collectFileIds(serialized);
    await IndexedDb.cleanupOrphanedItems(fileIds);
  } catch (e) {
    console.error("Failed to save to localStorage:", e);
  }
}
